import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BRAND = '#403EC9';

export default function InvestorSignup() {
  const [params]    = useSearchParams();
  const navigate    = useNavigate();
  const token       = params.get('token');

  const [tokenState, setTokenState] = useState('checking'); // checking | valid | invalid
  const [emailHint,  setEmailHint]  = useState('');
  const [form,       setForm]       = useState({ name:'', email:'', password:'', confirm:'' });
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [done,       setDone]       = useState(false);
  const [showPwd,    setShowPwd]    = useState(false);

  useEffect(() => {
    if (!token) { setTokenState('invalid'); return; }
    axios.get(`/api/v1/investor-invites/validate/${token}`)
      .then(r => {
        setTokenState('valid');
        if (r.data.data?.email_hint) {
          setEmailHint(r.data.data.email_hint);
          setForm(f => ({ ...f, email: r.data.data.email_hint }));
        }
      })
      .catch(err => {
        setTokenState('invalid');
        setError(err?.response?.data?.message || 'Invalid or expired invitation link.');
      });
  }, [token]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.password !== form.confirm) { setError('Passwords do not match.'); return; }
    if (form.password.length < 8)       { setError('Password must be at least 8 characters.'); return; }
    setLoading(true); setError('');
    try {
      await axios.post('/api/v1/investor-invites/signup', {
        token,
        name:     form.name.trim(),
        email:    form.email.toLowerCase(),
        password: form.password,
      });
      setDone(true);
    } catch(err) {
      setError(err?.response?.data?.message || 'Signup failed. Please try again.');
    } finally { setLoading(false); }
  };

  if (tokenState === 'checking') return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign:'center', color:'#6B7280', padding:32 }}>
          <div style={{ fontSize:'2rem', marginBottom:8 }}>🔗</div>
          <div>Verifying your invitation…</div>
        </div>
      </div>
    </div>
  );

  if (tokenState === 'invalid') return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign:'center', padding:24 }}>
          <div style={{ fontSize:'2.5rem', marginBottom:12 }}>❌</div>
          <div style={{ fontWeight:700, fontSize:'1.1rem', color:'#111827', marginBottom:8 }}>Invitation Invalid</div>
          <div style={{ color:'#6B7280', fontSize:'0.88rem', marginBottom:20 }}>
            {error || 'This invitation link is invalid, expired, or has already been used.'}
          </div>
          <div style={{ fontSize:'0.8rem', color:'#9CA3AF' }}>
            Contact <strong>info@alveo.fit</strong> to request a new invitation.
          </div>
        </div>
      </div>
    </div>
  );

  if (done) return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign:'center', padding:24 }}>
          <div style={{ fontSize:'2.5rem', marginBottom:12 }}>✅</div>
          <div style={{ fontWeight:800, fontSize:'1.15rem', color:'#111827', marginBottom:8 }}>Application Received</div>
          <div style={{ color:'#6B7280', fontSize:'0.88rem', lineHeight:1.7, marginBottom:20 }}>
            Your investor account has been created. Our team will review and activate your access — typically within 1–2 business days.
          </div>
          <div style={{ padding:'14px 18px', background:'#EEF2FF', borderRadius:10, fontSize:'0.82rem', color: BRAND, fontWeight:500, lineHeight:1.6 }}>
            📧 A confirmation email has been sent to <strong>{form.email}</strong>.<br/>
            <span style={{ color:'#6B7280', fontSize:'0.78rem', fontWeight:400 }}>You'll receive another email once your access is approved.</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, marginBottom:16 }}>
            <div style={{ width:34, height:34, background:BRAND, borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ color:'#fff', fontWeight:800, fontSize:'0.85rem' }}>A</span>
            </div>
            <span style={{ fontWeight:800, fontSize:'1.1rem', color:'#111827' }}>alveofit</span>
          </div>
          <div style={{ display:'inline-block', background:'#DCFCE7', color:'#166534', borderRadius:20, padding:'3px 12px', fontSize:'0.72rem', fontWeight:700, marginBottom:12 }}>
            ✓ Valid Invitation
          </div>
          <h1 style={{ margin:'0 0 6px', fontSize:'1.35rem', fontWeight:800, color:'#111827' }}>Create your investor account</h1>
          <p style={{ margin:0, color:'#6B7280', fontSize:'0.84rem', lineHeight:1.5 }}>
            You've been invited to access the alveofit investor portal — live revenue data, deals pipeline, and financial projections.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div style={{ marginBottom:16 }}>
            <label style={labelStyle}>Full Name</label>
            <input type="text" placeholder="Your full name" value={form.name} required
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              style={inputStyle} />
          </div>

          {/* Email */}
          <div style={{ marginBottom:16 }}>
            <label style={labelStyle}>Email Address</label>
            <input type="email" placeholder="you@example.com" value={form.email} required
              readOnly={!!emailHint}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              style={{ ...inputStyle, background: emailHint ? '#F9FAFB' : '#fff' }} />
          </div>

          {/* Password */}
          <div style={{ marginBottom:16 }}>
            <label style={labelStyle}>Password</label>
            <div style={{ position:'relative' }}>
              <input type={showPwd ? 'text' : 'password'} placeholder="Min 8 characters" value={form.password} required
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                style={{ ...inputStyle, paddingRight:42 }} />
              <button type="button" onClick={() => setShowPwd(s => !s)}
                style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#9CA3AF', fontSize:14 }}>
                {showPwd ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {/* Confirm */}
          <div style={{ marginBottom:20 }}>
            <label style={labelStyle}>Confirm Password</label>
            <input type="password" placeholder="Repeat password" value={form.confirm} required
              onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
              style={inputStyle} />
          </div>

          {error && (
            <div style={{ background:'#FEE2E2', color:'#991B1B', borderRadius:8, padding:'10px 14px', fontSize:'0.82rem', marginBottom:16, fontWeight:500 }}>
              ⚠ {error}
            </div>
          )}

          <button type="submit" disabled={loading}
            style={{ width:'100%', padding:'12px', background: BRAND, color:'#fff', border:'none', borderRadius:9, fontSize:'0.92rem', fontWeight:700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Creating account…' : 'Create Investor Account →'}
          </button>
        </form>

        <div style={{ marginTop:20, textAlign:'center', fontSize:'0.72rem', color:'#9CA3AF', lineHeight:1.6 }}>
          Access requires admin approval before you can sign in.<br/>
          Already approved? <a href="/login" style={{ color: BRAND }}>Sign in here</a>
        </div>
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight:'100vh', background:'linear-gradient(135deg,#EEF2FF 0%,#F5F3FF 100%)',
  display:'flex', alignItems:'center', justifyContent:'center', padding:24, fontFamily:'Inter,system-ui,sans-serif',
};
const cardStyle = {
  background:'#fff', borderRadius:16, padding:'36px 40px', width:'100%', maxWidth:420,
  boxShadow:'0 20px 60px rgba(0,0,0,0.12)',
};
const labelStyle = { display:'block', fontSize:'0.8rem', fontWeight:600, color:'#374151', marginBottom:5 };
const inputStyle  = {
  width:'100%', padding:'10px 13px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:'0.88rem',
  outline:'none', boxSizing:'border-box', color:'#111827',
};
